import { createContext, useContext, type ReactNode } from 'react'
import {
  MapStyleProvider,
  useMapStyle,
  type MapStyleContextValue,
} from 'tripPlanner/components/ChatMap/MapStyleContext'
import type { MapStyleMode } from 'tripPlanner/components/ChatMap/map3DConfig'

export interface MapInteractionContextValue {
  /** Called when a hotel card is hovered (from carousel or explore drawer). Updates isCardHoveredRef + hoveredHotelId. */
  onCardHover: (offerId: string | undefined) => void
  /** Called when a map marker is hovered. Guards against clearing hover while a card is actively hovered. */
  onMarkerHover: (markerId: string | undefined) => void
  /** Currently highlighted hotel/marker ID for visual sync between map and card lists */
  highlightedMarkerId: string | undefined
  /** Shared main-map style mode so drawer maps can stay in sync */
  mapStyleMode: MapStyleMode
  onMapStyleModeChange: (mode: MapStyleMode) => void
}

const MapInteractionContext = createContext<MapInteractionContextValue | undefined>(undefined)

interface MapInteractionProviderProps {
  value: MapInteractionContextValue
  children: ReactNode
}

/**
 * Page-level interaction sync (hover + style). Also mounts `MapStyleProvider` so
 * shared chat components can read style without importing this page module.
 */
function MapInteractionProvider({ value, children }: MapInteractionProviderProps) {
  return (
    <MapInteractionContext.Provider value={value}>
      <MapStyleProvider
        mapStyleMode={value.mapStyleMode}
        onMapStyleModeChange={value.onMapStyleModeChange}
      >
        {children}
      </MapStyleProvider>
    </MapInteractionContext.Provider>
  )
}

export function useMapInteraction(): MapInteractionContextValue {
  const ctx = useContext(MapInteractionContext)
  if (!ctx) throw new Error('useMapInteraction must be used within MapInteractionProvider')
  return ctx
}

/** Soft read for drawer maps that may render outside the provider in tests. */
export function useMapInteractionStyle(): MapStyleContextValue {
  return useMapStyle()
}

export {
  MapInteractionProvider,
}
